<template>
  <div>
    <template v-if="profile.updated_at == null">
      <div class="container container-sm header">
        {{ __("profile.first_login") }}
      </div>
      <div class="container container-sm alert alert-warning">
        {{ __("profile.first_login_warning") }}
      </div>
    </template>
    <div
      v-else-if="profile.updated_by != user.id"
      class="container container-sm alert alert-warning"
    >
      {{ __("profile.changed_warning") }}
    </div>

    <editable
      ref="profileForm"
      save-url="user/profile"
      :save-processor="save"
      :default-state="
        profile.updated_at != null && profile.updated_by != user.id
          ? 'edit'
          : 'show'
      "
      :initial-sub-state="isLoading ? 'loading' : null"
      :initial-data="profile"
      :initial-errors="errors"
      :children-validations="validation"
      container-class="container container-sm card profile mt-3"
      :no-value-text="__('main.common.no-value')"
      no-value-class="is-not-set"
      :use-default-alerts="false"
    >
      <template slot-scope="editable">
        <div class="row card__header">
          <div class="col">
            <div class="d-flex align-items-center mb-3 mr-1">
              <svg
                width="1.8em"
                height="1.8em"
                viewBox="0 0 16 16"
                class="bi bi-person-badge"
                fill="#51b67d"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  d="M2 2.5A2.5 2.5 0 0 1 4.5 0h7A2.5 2.5 0 0 1 14 2.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2.5zM4.5 1A1.5 1.5 0 0 0 3 2.5v10.795a4.2 4.2 0 0 1 .776-.492C4.608 12.387 5.937 12 8 12s3.392.387 4.224.803a4.2 4.2 0 0 1 .776.492V2.5A1.5 1.5 0 0 0 11.5 1h-7z"
                />
                <path
                  fill-rule="evenodd"
                  d="M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5z"
                />
              </svg>
              <h2 class="mb-0">{{ __("profile.contact.header") }}</h2>
            </div>
          </div>
        </div>
        <div class="row card__alerts">
          <div class="col">
            <div
              v-for="(alert, index) in editable.alerts"
              :key="index"
              :class="
                'card__alert card__alert--' +
                alert.type +
                ' alert alert-' +
                alert.type
              "
            >
              {{ alert.text }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col col-12 col-md-5 profile__data">
            <editable-input
              name="firstname"
              :tabindex="1"
              :label="__('profile.labels.firstname')"
            ></editable-input>
            <editable-input
              name="lastname"
              :tabindex="3"
              :label="__('profile.labels.lastname')"
            ></editable-input>
            <editable-date
              name="birthdate"
              :tabindex="6"
              :label="__('profile.labels.birthdate')"
            ></editable-date>
            <editable-input
              name="tel_office"
              :tabindex="8"
              :label="__('profile.labels.tel_office')"
            ></editable-input>
            <editable-input
              name="tel_mobile"
              :tabindex="10"
              :label="__('profile.labels.tel_mobile')"
            ></editable-input>
            <editable-input
              name="email"
              :tabindex="12"
              :label="__('profile.labels.email')"
            ></editable-input>
          </div>
          <div class="col col-12 col-md-5">
            <editable-input
              name="street"
              :tabindex="2"
              :label="__('profile.labels.street')"
            ></editable-input>

            <div class="row">
              <div class="col col-12 col-md-4">
                <editable-input
                  name="postal_code"
                  :tabindex="4"
                  :label="__('profile.labels.postal_code')"
                ></editable-input>
              </div>
              <div class="col col-12 col-md-8">
                <editable-input
                  name="city"
                  :tabindex="5"
                  :label="__('profile.labels.city')"
                ></editable-input>
              </div>
            </div>

            <editable-select
              name="country"
              :tabindex="7"
              :label="__('profile.labels.country')"
            >
              <option v-for="(text, key) in countries" :key="key" :value="key">
                {{ text }}
              </option>
            </editable-select>
            <editable-select
              name="nationality"
              :tabindex="9"
              :label="__('profile.labels.nationality')"
            >
              <option v-for="(text, key) in countries" :key="key" :value="key">
                {{ text }}
              </option>
            </editable-select>

            <editable-input
              name="license_plate"
              :tabindex="11"
              :label="__('profile.labels.license_plate')"
            ></editable-input>

            <editable-file
              name="id_card"
              :multiple="true"
              :tabindex="13"
              :label="__('profile.labels.id_card')"
              :label-file-select="__('profile.labels.btn_file_select')"
              label-file-select-class="btn btn-small btn-secondary mt-sm"
              :allowed-mime-types="[
                'image/jpg',
                'image/jpeg',
                'image/png',
                'application/pdf',
                'application/x-pdf',
              ]"
              @file-clicked="fileClicked"
            >
            </editable-file>
            <editable-file
              name="work_permit"
              :multiple="true"
              :tabindex="14"
              :label="__('profile.labels.work_permit')"
              :label-file-select="__('profile.labels.btn_file_select')"
              label-file-select-class="btn btn-small btn-secondary mt-sm"
              :allowed-mime-types="[
                'image/jpg',
                'image/jpeg',
                'image/png',
                'application/pdf',
                'application/x-pdf',
              ]"
              @file-clicked="fileClicked"
            >
            </editable-file>
          </div>
          <div class="col col-4 col-sm-3 col-md-2">
            <editable-image
              name="picture"
              :value="
                profile.picture
                  ? profile.picture +
                    '?t=' +
                    new Date(profile.updated_at).valueOf() / 1000
                  : ''
              "
              :placeholder-url="config.user.profilePictureFallback"
              :hooks="imageHooks"
            >
            </editable-image>
          </div>
        </div>

        <template v-if="forceEdit">
          <popover
            v-if="
              popovers.show &&
              editable.state == 'show' &&
              editable.subState != 'success'
            "
            key="popovershow"
            target=".editable__btn-toggle-state"
            relative-to="$parent"
            :hide-on-outside-click="false"
            @close="popovers.show = false"
          >
            Hier klicken um die Daten zu bearbeiten
          </popover>
          <popover
            v-if="
              popovers.edit &&
              editable.state == 'edit' &&
              editable.subState != 'success'
            "
            key="popoveredit"
            type="success"
            target=".editable__btn-toggle-state"
            relative-to="$parent"
            :hide-on-outside-click="false"
            @close="popovers.edit = false"
          >
            Erneut hier klicken um die Änderungen zu speichern
          </popover>
        </template>
      </template>
    </editable>
  </div>
</template>

<script>
import ViewBase from "./ViewBase";
import { mapState } from "vuex";
import api from "../helper/api";
import * as countries from "smartdoors-shared/countries.json";

export default {
  extends: ViewBase,
  provide() {
    return {
      baseUrl: this.env.PORTAL_URL + "/files/download/",
    };
  },
  data() {
    return {
      model: "profile",
      popovers: {
        show: true,
        edit: true,
      },
      isLoading: false,
    };
  },
  computed: {
    profile() {
      return this.$store.getters["user/profile"];
    },
    errors() {
      return this.$store.getters["alerts/view"]("profile");
    },
    countries() {
      return Object.fromEntries(
        Object.entries(countries.default).map(([ck, cv]) => {
          return [ck, cv[this.appLocale]];
        })
      );
    },
    imageHooks() {
      return {
        beforeUploading: () => {
          this.app.isLoading = true;
        },
        onError: () => {
          this.app.isLoading = false;
        },
        uploadedImageLoaded: () => {
          this.app.isLoading = false;
        },
      };
    },
    ...mapState("user", ["userDataUpdating"]),
  },
  watch: {
    userDataUpdating: {
      immediate: true,
      handler: async function (newUpdatingPromise) {
        if (newUpdatingPromise) {
          // hide the loader after user data got reloaded from api (triggered by a positive connection check)
          // loader should be enabled before by the handleConnectionChecking method
          await newUpdatingPromise;
          this.isLoading = false;
        }
      },
    },
  },
  created() {
    this.forceEdit =
      this.profile.updated_at == null ||
      this.profile.updated_by != this.user.id;

    // watch for connection checking, since it may trigger a reload of the user profile data
    this.app.$watch("checkingConnection", this.handleConnectionChecking);
    this.handleConnectionChecking(this.app.checkingConnection);
  },
  methods: {
    fileClicked($event) {
      $event.event.preventDefault();

      const path =
        this.config.api.urls.files.download + "/" + $event.file.route;

      this.$refs.profileForm.setState(this.$refs.profileForm.state, "loading");

      api.download(path).then(
        (url) => {
          this.$refs.profileForm.setState(this.$refs.profileForm.state, null);

          if ($event.file.showable) {
            this.$windows.showFilesWindow($event.label, [
              Object.assign($event.file, { src: url }),
            ]);
          } else {
            const a = document.createElement("a");
            a.href = url;
            a.download = $event.file.name;
            a.click();
            URL.revokeObjectURL(url);
          }
        },
        (response) => {
          this.$refs.profileForm.setState(this.$refs.profileForm.state, null);
          this.$refs.profileForm.setErrors(response.valueOf());
        }
      );
    },
    save(url, data) {
      return new Promise((resolve, reject) => {
        this.$store
          .dispatch(
            "tasks/runUnique",
            this.api
              .post(url, data, true)
              .name("profileUpdate")
              .data({ view: "Profile" })
          )
          // on deferred: resolve save processor and return input data for next then
          .deferred(() => {
            // disallow updating user data from source until task is executed
            this.$store.commit("user/disallowUpdateFromSource");
            // add deferred warning to profile alerts
            this.$store.commit("alerts/profile", [
              {
                type: "warning",
                text: this.__("main.common.messages.save_deferred"),
              },
            ]);
            // resolve save processor with empty object
            resolve({});

            return data;
          })
          // always commit update to store on resolve
          .thenAlways(["store", "commit:user/updateProfile"])
          // on non-deferred resolve: resolve save processor and redirect if user has been forced to edit his profile
          .then((apiResponse) => {
            resolve({}); // resolve save with empty response data because updated data will come from the store

            if (this.forceEdit) {
              window.setTimeout(() => {
                this.$router.push({ name: "Dashboard" });
              }, 500);
            }

            return apiResponse;
          })
          // on deferred resolve: clear profile alerts
          .thenDeferred(["store", "commit:alerts/profile", null])
          // on non-deferred reject: reject save processor with raw response object
          .catch((apiResponse) => {
            if (apiResponse.networkError) {
              reject(apiResponse.message);
            } else {
              resolve(apiResponse.rawResponse);
            }
          })
          // on deferred reject commit alerts to store
          .catch(["store", "commit:alerts/profile"])
          // after deferred: allow updating user data from source again
          .finallyDeferred(["store", "commit:user/allowUpdateFromSource"]);
      });
    },
    /**
     * Handles running connection checks.
     * Shows the loading indicator since a positive connection check will trigger a user profile data reload.
     * The user should not be able to interact with the profile editable until the data got loaded.
     *
     * @param {Promise} connectionChecking
     */
    async handleConnectionChecking(connectionChecking) {
      if (connectionChecking) {
        // show the loader until the user profile data got loaded (see userDataUpdating watcher)
        this.isLoading = true;

        try {
          await connectionChecking;
        } catch (err) {
          // if the connection check fails, there will be no reload of the user profile data, so we can hide the loader again
          this.isLoading = false;
        }
      }
    },
  },
};
</script>
<style lang="scss">
.profile {
  padding: 15px;
  .card__header {
    h2 {
      font-size: 1.6rem;
    }
    svg {
      margin-right: 0.4rem;
    }
  }
}
</style>